import { useState } from "react";
import { Shirt, MoreHorizontal } from "lucide-react";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import { DataTable } from "../../ui/data-table/data-table";
import { DataTableColumnHeader } from "../../ui/data-table/data-table-column-header";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "../../ui/dropdown-menu";
import { ExportMenu } from "../../ui/export-menu";
import { SummaryHeader } from "../../ui/SummaryHeader";
import { AlertTriangle, CheckCircle2, AlertCircle, TrendingDown } from "lucide-react";
import { useInventoryAnalysis } from "../hooks/useInventoryAnalysis";
import ManagerialLoader from "../../../components/loaders/ManagerialLoader";

interface InventoryItem {
  style?: string;
  style_description: string;
  color: string;
  inv_deplete_days_OH: number;
  sales_by_day: number;
  status?: string; // synthetic
}

export function ProductLifecycleTable() {
  const [activeTab, setActiveTab] = useState<"negative" | "restock">("negative");

  const { data, isLoading } = useInventoryAnalysis({
    source: "shopify",
    threshold_days_OS: 30,
    inv_type: "OH",
    days_look: 30,
  });

  const inventoryData: InventoryItem[] =
    (activeTab === "negative" ? data?.alerts?.negative_inventory : data?.alerts?.restock) || [];

  const processedData = inventoryData.map((item) => {
    let status: string;
    if (item.inv_deplete_days_OH < 0) status = "critical";
    else if (item.inv_deplete_days_OH < 5) status = "restock_soon";
    else status = "healthy";
    return { ...item, status };
  });

  const columns = [
    {
      accessorKey: "style_description",
      header: ({ column }: any) => <DataTableColumnHeader column={column} title="Product" />,
      cell: ({ row }: any) => (
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-zinc-100 flex items-center justify-center">
            <Shirt className="h-4 w-4 text-zinc-500" />
          </div>
          <div>
            <div className="font-medium">{row.getValue("style_description")}</div>
            <div className="text-xs text-muted-foreground">{row.original.style}</div>
          </div>
        </div>
      ),
    },
    {
      accessorKey: "color",
      header: ({ column }: any) => <DataTableColumnHeader column={column} title="Color" />,
    },
    {
      accessorKey: "status",
      header: ({ column }: any) => <DataTableColumnHeader column={column} title="Status" showSortingOptions={false} />,
      cell: ({ row }: any) => (
        <Badge
          variant="outline"
          className={getStatusBadgeClass(row.getValue("status"))}
        >
          <StatusIcon status={row.getValue("status")} className="mr-1 h-3 w-3" />
          {formatStatus(row.getValue("status"))}
        </Badge>
      ),
    },
    {
      accessorKey: "sales_by_day",
      header: ({ column }: any) => <DataTableColumnHeader column={column} title="Sales/Day" />,
      cell: ({ row }: any) => `${row.getValue("sales_by_day")} units/day`,
    },
    {
      accessorKey: "inv_deplete_days_OH",
      header: ({ column }: any) => <DataTableColumnHeader column={column} title="Depletion Days" />,
      cell: ({ row }: any) => {
        const value = row.getValue("inv_deplete_days_OH");
        const className =
          value < 0
            ? "text-red-600 font-medium"
            : value < 5
            ? "text-amber-600"
            : "";
        return <div className={className}>{value} days</div>;
      },
    },
    {
      id: "actions",
      cell: ({ row }: any) => {
        const item = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => navigator.clipboard.writeText(item.style || "N/A")}
              >
                Copy Style ID
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <>
      <SummaryHeader
        title="Inventory"
      />
      <Card>
        <div className="flex flex-col sm:flex-row gap-2 p-4">
          <div className="flex gap-2">
            <Button
              onClick={() => setActiveTab("negative")}
              variant={activeTab === "negative" ? "default" : "outline"}
              size="sm"
            >
              Negative Inventory
            </Button>
            <Button
              onClick={() => setActiveTab("restock")}
              variant={activeTab === "restock" ? "default" : "outline"}
              size="sm"
            >
              Restock Soon
            </Button>
          </div>
          <div className="w-full flex justify-end gap-2">
            <ExportMenu
              data={processedData}
              filename={`inventory_${activeTab}`}
              buttonSize="sm"
              label=""
            />
          </div>
        </div>
        {isLoading ? (
          <ManagerialLoader variant="product-cycle" />
        ) : (
          <CardContent className="p-4">
            <DataTable
              columns={columns}
              data={processedData}
              searchColumn="style_description"
              searchPlaceholder="Search Product..."
              initialPageSize={10}
            />
          </CardContent>
        )}
      </Card>
    </>
  );
}

function getStatusBadgeClass(status: string) {
  switch (status) {
    case "healthy":
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
    case "restock_soon":
      return "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300";
    case "critical":
      return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
    default:
      return "";
  }
}

function formatStatus(status: string) {
  return status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function StatusIcon({
  status,
  className,
}: {
  status: string;
  className?: string;
}) {
  switch (status) {
    case "healthy":
      return <CheckCircle2 className={className} />;
    case "restock_soon":
      return <AlertTriangle className={className} />;
    case "critical":
      return <AlertCircle className={className} />;
    default:
      return <TrendingDown className={className} />;
  }
}
